import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowRight, Search, ChevronDown, Trash2, AlertTriangle, X as XIcon, Image as ImageIcon, Video as VideoIcon, FileText, User } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import PhaseChip from "../components/PhaseChip";
import Modal from "../components/Modal";
import { useAuth } from "../context/AuthContext";
import { usePersistedState } from "../lib/persisted";
import { TICKET_STATUSES } from "../lib/enums";
import { uploadUrl } from "../lib/uploads";
import VoiceQuickCreate from "../components/VoiceQuickCreate";
import UnseenDot from "../components/UnseenDot";
import EntityAlertBadge from "../components/EntityAlertBadge";
import useUnseen from "../lib/useUnseen";
import useEntityAlerts from "../lib/useEntityAlerts";
import useFocusOnMount from "../lib/useFocusOnMount";
import JobCardPickerModal from "../components/JobCardPickerModal";

// "Closed" statuses are hidden from the default Tickets list view.
const CLOSED_STATUSES = new Set(["resolved", "resolved_via_jc", "resolved_external", "closed", "cancelled"]);
// Group order on the list page (keeps active statuses on top)
const STATUS_ORDER = TICKET_STATUSES.reduce((acc, s, i) => ({ ...acc, [s.key]: i }), {});

export default function Tickets() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "admin" || user?.role === "manager";
  const [tickets, setTickets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [showClosed, setShowClosed] = usePersistedState("tickets.showClosed", false);
  const [collapsed, setCollapsed] = usePersistedState("tickets.collapsed", {});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const navigate = useNavigate();

  const load = async (opts) => {
    const silent = !!opts?.silent;
    if (!silent) {
      setLoadError("");
      setLoading(true);
    }
    // Same pattern as JobCards — render tickets first, never block on the
    // heavy customer/site/user lookups (each ~700KB on slow mobile nets).
    try {
      const t = await api.get("/tickets");
      setTickets(Array.isArray(t.data) ? t.data : []);
      if (silent) setLoadError("");
    } catch (e) {
      if (!silent) {
        setLoadError(e?.response?.data?.detail || e?.message || "Could not load tickets.");
      }
    } finally {
      if (!silent) setLoading(false);
    }
    api.get("/customers").then((r) => setCustomers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/sites").then((r) => setSites(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/users").then((r) => {
      setUsers((r.data || []).filter((x) => x.role && x.role !== "client" && x.active !== false));
    }).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);
  // Auto-refresh while the tab is foreground (Wave 19 — concurrent-edit safety).
  useAutoRefresh(load, { intervalMs: 30_000 });

  // Awareness Contract — DESIGN_CONTRACT §7.
  const { isUnseen, touch, dismiss } = useUnseen("tickets", tickets);
  useEffect(() => {
    touch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useFocusOnMount({ ready: tickets.length > 0, deps: [tickets.length] });

  // Wave 19 #84 — directed-alert pulses per row (rose badge with count).
  const { unreadFor: ticketUnreadFor } = useEntityAlerts("ticket");

  const updateStatus = async (t, status) => {
    // Iter 418.be (Dirk, 2026-02-28) — inline "Resolve via JC" on the
    // list view now opens the same picker as the detail page. Prevents
    // the bug where flipping status here silently bypassed the link
    // requirement.
    if (status === "resolved_via_jc" && !t.linked_job_card_id) {
      setJcPickerFor(t);
      return;
    }
    await api.put(`/tickets/${t.id}`, { ...t, status });
    load();
  };

  const [jcPickerFor, setJcPickerFor] = useState(null);
  const linkJcAndResolve = async (jc) => {
    if (!jc || !jcPickerFor) return;
    try {
      await api.put(`/tickets/${jcPickerFor.id}`, {
        ...jcPickerFor,
        status: "resolved_via_jc",
        linked_job_card_id: jc.id,
      });
    } finally {
      setJcPickerFor(null);
    }
    load();
  };

  const convert = async (t) => {
    const { data } = await api.post(`/tickets/${t.id}/convert`, {});
    navigate(`/jobcards/${data.id}`);
  };

  const remove = async () => {
    if (!confirmDelete) return;
    await api.delete(`/tickets/${confirmDelete.id}`);
    setConfirmDelete(null);
    load();
  };

  const customerName = (id) => {
    if (!id) return "—";
    const c = customers.find((x) => x.id === id);
    if (c) return c.name || `Customer ${id.slice(0, 6)}`;
    return `Customer ${id.slice(0, 6)}…`;
  };
  const customerRef = (id) => customers.find((c) => c.id === id)?.external_ref;

  const visibleTickets = useMemo(() => {
    const term = (q || "").trim().toLowerCase();
    return tickets.filter((t) => {
      if (!showClosed && CLOSED_STATUSES.has(t.status)) return false;
      if (!term) return true;
      const hay = [
        t.subject, t.description, t.status, t.support_type, t.external_ref,
        customerName(t.customer_id), customerRef(t.customer_id),
      ].map((x) => (x ? String(x).toLowerCase() : "")).join(" | ");
      return hay.includes(term);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, q, showClosed, customers]);

  const groupedTickets = useMemo(() => {
    const groups = {};
    for (const t of visibleTickets) {
      (groups[t.status] = groups[t.status] || []).push(t);
    }
    // Within each status group, newest first by created_at
    for (const k of Object.keys(groups)) {
      groups[k].sort((a, b) => {
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return tb - ta;
      });
    }
    return Object.entries(groups).sort((a, b) => (STATUS_ORDER[a[0]] ?? 99) - (STATUS_ORDER[b[0]] ?? 99));
  }, [visibleTickets]);

  const toggleCollapse = (key) => setCollapsed({ ...collapsed, [key]: !collapsed[key] });

  return (
    <div className="space-y-5" data-testid="tickets-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">After-sales</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" />
            Support tickets
            <span className="text-base font-normal text-[#647087]">· {visibleTickets.length}</span>
          </h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search subject, customer, status…"
              className="hv-input pl-8 py-2 text-xs w-72"
              data-testid="tickets-search"
            />
          </div>
          <label className="text-xs text-[#a0aabf] inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showClosed}
              onChange={(e) => setShowClosed(e.target.checked)}
              data-testid="tickets-show-closed"
            />
            Show closed
          </label>
          <Link
            to="/tickets/new"
            className="hv-btn hv-btn-primary"
            data-testid="ticket-new-btn"
            title="Open the full New Ticket form"
          >
            <Plus className="w-4 h-4" /> New ticket
          </Link>
          <VoiceQuickCreate
            kind="ticket"
            onCreate={async ({ title, description }) => {
              const params = new URLSearchParams();
              if (title) params.set("subject", title);
              if (description) params.set("description", description);
              navigate(`/tickets/new?${params.toString()}`);
              return { ok: true };
            }}
          />
        </div>
      </div>

      {loading && (
        <div className="hv-card p-6 text-center" data-testid="tickets-loading">
          <div className="text-sm text-[#a0aabf]">Loading tickets…</div>
        </div>
      )}
      {!loading && loadError && (
        <div className="hv-card p-4 border border-rose-500/30 bg-rose-500/5" data-testid="tickets-error">
          <div className="text-sm text-rose-300 font-medium">Could not load tickets.</div>
          <div className="text-xs text-[#a0aabf] mt-1">{loadError}</div>
          <button
            type="button"
            onClick={load}
            className="hv-btn hv-btn-ghost text-xs mt-2"
            data-testid="tickets-retry"
          >
            Try again
          </button>
        </div>
      )}

      <div className="space-y-3">
        {groupedTickets.map(([status, items]) => {
          const isCollapsed = collapsed[status];
          return (
            <div key={status} className="hv-card overflow-hidden" data-testid={`ticket-group-${status}`}>
              <button
                onClick={() => toggleCollapse(status)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition"
                data-testid={`ticket-fold-${status}`}
              >
                <div className="flex items-center gap-2">
                  <ChevronDown className={`w-4 h-4 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                  <PhaseChip value={status} />
                  <span className="text-sm text-[#a0aabf]">{items.length} ticket{items.length === 1 ? "" : "s"}</span>
                </div>
              </button>
              {!isCollapsed && (
                <>
                {/* Desktop / tablet: table layout */}
                <table className="hidden md:table w-full text-sm border-t border-white/10">
                  <tbody>
                    {items.map((t) => {
                      const logged = t.created_at ? new Date(t.created_at) : null;
                      const ageDays = logged
                        ? Math.floor((Date.now() - logged.getTime()) / (1000 * 60 * 60 * 24))
                        : null;
                      const ageColor =
                        ageDays === null
                          ? "text-[#647087] border-white/10 bg-white/[0.03]"
                          : ageDays >= 14
                          ? "text-rose-300 border-rose-500/40 bg-rose-500/10"
                          : ageDays >= 7
                          ? "text-amber-300 border-amber-500/40 bg-amber-500/10"
                          : ageDays >= 3
                          ? "text-yellow-200 border-yellow-500/30 bg-yellow-500/5"
                          : "text-emerald-300 border-emerald-500/30 bg-emerald-500/5";
                      return (
                      <tr
                        key={t.id}
                        className={`border-b border-white/5 hover:bg-rose-500/5 transition cursor-pointer ${isUnseen(t) ? "hv-unseen" : ""}`}
                        data-testid={`ticket-row-${t.id}`}
                        data-focus-id={t.id}
                        onClick={() => { dismiss(t.id); navigate(`/tickets/${t.id}`); }}
                      >
                        <td className="px-4 py-3">
                          {t.short_id && (
                            <div className="flex items-center gap-2">
                              <div className="text-[10px] font-mono text-rose-300">#{t.short_id}</div>
                              <EntityAlertBadge count={ticketUnreadFor(t.id)} testId={`ticket-list-alerts-${t.id}`} />
                            </div>
                          )}
                          <div className="font-medium hover:text-rose-300 transition flex items-center gap-2">
                            <UnseenDot unseen={isUnseen(t)} testId={`ticket-unseen-${t.id}`} />
                            {t.subject}
                            {(t.notes || []).length > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-300">
                                {t.notes.length} notes
                              </span>
                            )}
                          </div>
                          {t.description && (
                            <div className="text-xs text-[#647087] truncate max-w-md">{t.description}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[#a0aabf]">
                          <Link
                            to={`/customers/${t.customer_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="hover:text-[#1fb6c1] transition"
                          >
                            {customerName(t.customer_id)}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-[#a0aabf] whitespace-nowrap" data-testid={`ticket-assignee-${t.id}`}>
                          {t.assigned_to_id ? (
                            <span className="inline-flex items-center gap-1 text-xs">
                              <User className="w-3 h-3 text-[#1fb6c1]" />
                              {(users.find((u) => u.id === t.assigned_to_id)?.name) || "—"}
                            </span>
                          ) : (
                            <span className="text-[10px] text-[#647087] italic">unassigned</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" data-testid={`ticket-logged-${t.id}`}>
                          {logged ? (
                            <div className="flex flex-col items-start gap-0.5">
                              <span className="text-xs text-[#dde3ec] font-mono">
                                {logged.toLocaleDateString('en-GB')}
                              </span>
                              <span
                                className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border font-bold ${ageColor}`}
                                title={`Logged ${logged.toLocaleString('en-GB')}`}
                              >
                                {ageDays === 0 ? "today" : ageDays === 1 ? "1 day" : `${ageDays} days`}
                              </span>
                            </div>
                          ) : (
                            <span className="text-[10px] text-[#647087]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={t.status}
                            onChange={(e) => updateStatus(t, e.target.value)}
                            className="hv-input py-1 text-xs"
                            data-testid={`ticket-status-${t.id}`}
                          >
                            {TICKET_STATUSES.map((s) => (
                              <option key={s.key} value={s.key}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-2">
                            {!CLOSED_STATUSES.has(t.status) && (
                              <button
                                onClick={() => convert(t)}
                                className="hv-btn hv-btn-ghost text-xs"
                                data-testid={`ticket-convert-${t.id}`}
                              >
                                Convert <ArrowRight className="w-3 h-3" />
                              </button>
                            )}
                            {isManager && (
                              <button
                                onClick={() => setConfirmDelete({ id: t.id, subject: t.subject })}
                                className="hv-btn hv-btn-ghost text-rose-300 hover:text-rose-200 px-2 py-1 text-xs"
                                data-testid={`ticket-delete-${t.id}`}
                                title="Delete ticket"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Mobile: card layout */}
                <ul className="md:hidden divide-y divide-white/5 border-t border-white/10">
                  {items.map((t) => {
                    const logged = t.created_at ? new Date(t.created_at) : null;
                    const ageDays = logged
                      ? Math.floor((Date.now() - logged.getTime()) / (1000 * 60 * 60 * 24))
                      : null;
                    const ageColor =
                      ageDays === null
                        ? "text-[#647087] border-white/10 bg-white/[0.03]"
                        : ageDays >= 14
                        ? "text-rose-300 border-rose-500/40 bg-rose-500/10"
                        : ageDays >= 7
                        ? "text-amber-300 border-amber-500/40 bg-amber-500/10"
                        : ageDays >= 3
                        ? "text-yellow-200 border-yellow-500/30 bg-yellow-500/5"
                        : "text-emerald-300 border-emerald-500/30 bg-emerald-500/5";
                    return (
                      <li
                        key={t.id}
                        className={`px-3 py-3 active:bg-rose-500/10 transition cursor-pointer ${isUnseen(t) ? "hv-unseen" : ""}`}
                        data-testid={`ticket-card-${t.id}`}
                        data-focus-id={t.id}
                        onClick={() => { dismiss(t.id); navigate(`/tickets/${t.id}`); }}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="min-w-0 flex-1">
                            {t.short_id && (
                              <div className="flex items-center gap-2">
                                <div className="text-[10px] font-mono text-rose-300">#{t.short_id}</div>
                                <EntityAlertBadge count={ticketUnreadFor(t.id)} testId={`ticket-mobile-alerts-${t.id}`} />
                              </div>
                            )}
                            <div className="font-medium leading-snug flex items-center gap-2">
                              <UnseenDot unseen={isUnseen(t)} testId={`ticket-unseen-mobile-${t.id}`} />
                              {t.subject}
                            </div>
                            <div className="text-[11px] text-[#a0aabf] mt-0.5">
                              <Link
                                to={`/customers/${t.customer_id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="hover:text-[#1fb6c1]"
                              >
                                {customerName(t.customer_id)}
                              </Link>
                            </div>
                          </div>
                          {logged && (
                            <span
                              className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border font-bold shrink-0 ${ageColor}`}
                            >
                              {ageDays === 0 ? "today" : ageDays === 1 ? "1d" : `${ageDays}d`}
                            </span>
                          )}
                        </div>
                        {t.description && (
                          <div className="text-[11px] text-[#647087] line-clamp-2 mb-2">
                            {t.description}
                          </div>
                        )}
                        <div
                          className="flex items-center gap-2 flex-wrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <select
                            value={t.status}
                            onChange={(e) => updateStatus(t, e.target.value)}
                            className="hv-input py-1 text-[11px] flex-1 min-w-0"
                            data-testid={`ticket-status-mobile-${t.id}`}
                          >
                            {TICKET_STATUSES.map((s) => (
                              <option key={s.key} value={s.key}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                          {!CLOSED_STATUSES.has(t.status) && (
                            <button
                              type="button"
                              onClick={() => convert(t)}
                              className="hv-btn hv-btn-ghost text-[11px] py-1 px-2"
                              data-testid={`ticket-convert-mobile-${t.id}`}
                            >
                              Convert <ArrowRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                </>
              )}
            </div>
          );
        })}
        {visibleTickets.length === 0 && (
          <div className="hv-card p-6 text-center text-[#647087]">No tickets to show.</div>
        )}
      </div>

      {confirmDelete && (
        <Modal open onClose={() => setConfirmDelete(null)} title="Delete ticket" testId="ticket-delete-modal" maxWidth="max-w-md">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-rose-500/15 border border-rose-500/40 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-300" />
              </div>
              <div>
                <p className="text-sm">
                  Permanently delete ticket <span className="font-bold text-white">"{confirmDelete.subject}"</span>?
                </p>
                <p className="text-xs text-[#a0aabf] mt-1">
                  This cannot be undone. The ticket and its notes will be removed for all users.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
              <button onClick={() => setConfirmDelete(null)} className="hv-btn hv-btn-ghost" data-testid="ticket-delete-cancel">
                Cancel
              </button>
              <button onClick={remove} className="hv-btn hv-btn-primary bg-rose-500 hover:bg-rose-400" data-testid="ticket-delete-confirm">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </Modal>
      )}

      <NewTicketDialogShim />
      <JobCardPickerModal
        open={!!jcPickerFor}
        customerId={jcPickerFor?.customer_id}
        onPick={linkJcAndResolve}
        onClose={() => setJcPickerFor(null)}
      />
    </div>
  );
}

// Backwards-compat shim — the modal was removed in 2026-05-03 #17 in favour
// of the full-page /tickets/new editor. Keeping a no-op placeholder here so
// any future ref doesn't fail; deletes when stale references are gone.
function NewTicketDialogShim() { return null; }


function CrmAttachmentChip({ att, onRemove }) {
  const url = uploadUrl(att.id);
  const ct = att.content_type || "";
  const isImage = ct.startsWith("image/") || att.kind === "image";
  const isVideo = ct.startsWith("video/") || att.kind === "video";
  const isAudio = ct.startsWith("audio/") || att.kind === "audio";
  return (
    <div className="relative group rounded border border-white/10 overflow-hidden bg-black/30 aspect-square">
      {isImage ? (
        <img src={url} alt={att.filename} className="w-full h-full object-cover" />
      ) : isVideo ? (
        <video src={url} className="w-full h-full object-cover bg-black" muted preload="metadata" />
      ) : isAudio ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-[#1fb6c1] p-2">
          <VideoIcon className="w-6 h-6 opacity-60" />
          <div className="text-[9px] mt-1 truncate w-full text-center text-[#a0aabf]">
            {att.filename}
          </div>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-[#647087] p-2">
          <FileText className="w-6 h-6" />
          <div className="text-[9px] mt-1 truncate w-full text-center">{att.filename}</div>
        </div>
      )}
      {isVideo && (
        <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] uppercase rounded px-1">
          <VideoIcon className="w-3 h-3 inline" />
        </span>
      )}
      {isImage && (
        <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] uppercase rounded px-1">
          <ImageIcon className="w-3 h-3 inline" />
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 bg-black/70 text-rose-300 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
        title="Remove"
        data-testid={`ticket-modal-media-remove-${att.id}`}
      >
        <XIcon className="w-3 h-3" />
      </button>
    </div>
  );
}
